import Seo from '@/components/base/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollTopButton from '@/components/feature/ScrollTopButton';
import { siteName, siteSlogan, siteDesc, testimonials } from '@/mocks/siteData';

const principles = [
  { icon: 'ri-sparkling-line', title: '清晰标注剧透', desc: '隐藏内容攻略严格区分主线指引与剧情详解，首周目玩家不会被提前剧透。' },
  { icon: 'ri-hand-coin-line', title: '条件可复现', desc: '每个触发条件都经过验证，标注隐藏数值机制与避免踩坑的减分项。' },
  { icon: 'ri-heart-pulse-line', title: '为剧情党而写', desc: '以角色扮演与故事体验为核心，不堆砌无意义的数值教程。' },
  { icon: 'ri-refresh-line', title: '持续更新', desc: '随同人作品更新同步修订攻略，保证版本准确。' },
];

const values = [
  { icon: 'ri-compass-3-line', title: '深度理解每部作品', desc: '从世界观、角色动机到多结局设计，真正吃透作品内核。' },
  { icon: 'ri-group-line', title: '陪伴玩家共同成长', desc: '不是冷冰冰的攻略机器，而是愿意回答问题的同行者。' },
  { icon: 'ri-quill-pen-line', title: '尊重作者表达', desc: '攻略帮助理解剧情，而非用「最优解」抹杀作品的叙事表达。' },
];

export default function AboutPage() {
  const jsonLd: Record<string, unknown>[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: `关于${siteName}`,
      description: siteDesc,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteName,
      slogan: siteSlogan,
      description: siteDesc,
    },
  ];

  return (
    <>
      <Seo
        title={`关于我们 - ${siteName} | 火影同人游戏攻略站`}
        description={`${siteName}是面向剧情党的火影同人RPG游戏攻略站，以清晰、可复现、尊重表达的攻略内容，帮助玩家玩透每一条故事线。`}
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 pb-16 pt-28 md:px-6 md:pt-32">
        <nav aria-label="面包屑" className="mb-8 flex flex-wrap items-center gap-1 font-label text-xs text-foreground-500">
          <a href="/" className="hover:text-primary-600">首页</a>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900">关于我们</span>
        </nav>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <p className="font-label text-sm font-bold tracking-widest text-accent-600">关于我们</p>
            <h1 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">{siteName}：把每条故事线都玩透</h1>
            <p className="mt-5 text-sm leading-relaxed text-foreground-700">
              {siteName}是一群火影同人RPG玩家的「剧情探路笔记」。我们相信，一部好的同人作品，最珍贵的不是战斗数值，而是那些藏在选择背后的角色动机、羁绊与真相。因此我们专注整理剧情向角色扮演体验，帮剧情党找到隐藏剧情、看懂多结局设计。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700">
              每一条攻略，都从一个「漏掉关键情节」的遗憾开始，到「原来如此」的恍然大悟结束。
            </p>
          </div>
          <div className="overflow-hidden rounded-lg">
            <img
              src="https://narutsur.github.io/api/search-image?query=Stylized%20anime%20ink%20illustration%20of%20a%20wooden%20desk%20littered%20with%20scrolls%2C%20ink%20brushes%2C%20paper%20maps%20and%20a%20glowing%20orange%20lantern%2C%20warm%20amber%20and%20crimson%20palette%20on%20deep%20ink%20background%2C%20storytelling%20guide%20theme%2C%20cozy%20painterly%20detail&width=900&height=600&seq=about-2026&orientation=landscape"
              alt="忍界攻略录的卷轴与笔墨，记录每一段剧情"
              className="h-full w-full object-cover object-center"
            />
          </div>
        </div>

        <section className="mt-16 border-t border-background-200 pt-12">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">我们相信并坚持的</h2>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className={`${v.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 text-sm font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16 bg-foreground-950 px-4 py-12 rounded-lg md:px-8">
          <h2 className="text-center font-heading text-xl font-bold text-background-50 md:text-2xl">内容原则</h2>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p) => (
              <div key={p.title} className="rounded-md border border-foreground-800 bg-foreground-900/40 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-500/20 text-accent-300">
                  <i className={`${p.icon} text-lg`}></i>
                </span>
                <h3 className="mt-3 text-sm font-bold text-background-50">{p.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-300">{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">剧情党的声音</h2>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.id} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <div className="flex items-center gap-1 text-accent-500">
                  {[1, 2, 3, 4, 5].map((n) => <i key={n} className="ri-star-fill text-sm"></i>)}
                </div>
                <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">“{t.text}”</blockquote>
                <figcaption className="mt-4 border-t border-background-200 pt-4 text-sm font-bold text-foreground-950">{t.name} · {t.role}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="mt-16 rounded-lg border border-background-200 bg-background-50 p-6 md:p-10">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">联系我们</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                有想补充的作品、想讨论的结局，或是发现攻略需要修订，都欢迎与我们联系。
              </p>
              <div className="mt-5 space-y-3 text-sm">
                <p className="flex items-center gap-3 text-foreground-700">
                  <i className="ri-mail-line text-accent-600"></i>
                  <a href="mailto:contact@example.com" className="hover:text-primary-600">contact@example.com</a>
                </p>
                <p className="flex items-center gap-3 text-foreground-700">
                  <i className="ri-question-line text-accent-600"></i>
                  优先回复与攻略内容相关的建议与勘误
                </p>
              </div>
            </div>
            <div className="rounded-lg bg-background-100 p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950">我们的定位</h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                {siteName}不是泛泛的资讯平台，而是专注「剧情向角色扮演」这一细分领域的内容站。我们相信深度比广度更有价值，愿做每一位剧情党旅途中的可靠同行者。
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollTopButton />
    </>
  );
}